import { useEffect, useCallback } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { userState } from "../recoil/auth";
import {
  notificationListState,
  unreadNotificationCountState,
} from "../recoil/notification";
import {
  getWebMemberNotificationAPI,
  readWebMemberNotificationAPI,
} from "../api";

export const useNotification = () => {
  const user = useRecoilValue(userState);
  const [notificationList, setNotificationList] = useRecoilState(
    notificationListState
  );
  const [unreadCount, setUnreadCount] = useRecoilState(
    unreadNotificationCountState
  );

  // 알림 목록 가져오기
  const fetchNotifications = useCallback(
    async (page = 1) => {
      if (!user?.id) return;

      try {
        setNotificationList((prev) => ({
          ...prev,
          loading: true,
          error: null,
        }));

        const response = await getWebMemberNotificationAPI(user.id, page, 20);
        const data = response.data;

        setNotificationList({
          list: data.list || [],
          total: data.total || 0,
          page: data.page || 1,
          pageSize: data.pageSize || 20,
          loading: false,
          error: null,
        });

        // 읽지 않은 알림 개수 계산
        const unreadNotifications =
          data.list?.filter((notification) => !notification.is_read) || [];
        setUnreadCount(unreadNotifications.length);
      } catch (error) {
        console.error("알림 목록 가져오기 실패:", error);
        setNotificationList((prev) => ({
          ...prev,
          loading: false,
          error: "알림을 불러오는데 실패했습니다.",
        }));
      }
    },
    [user?.id, setNotificationList, setUnreadCount]
  );

  // 알림 읽음 처리
  const markAsRead = useCallback(
    async (wn_id) => {
      if (!user?.id) {
        return;
      }

      try {
        await readWebMemberNotificationAPI(wn_id, true);

        // 로컬 상태 업데이트
        setNotificationList((prev) => {
          const updatedList = prev.list.map((notification) =>
            notification.wn_id === wn_id
              ? { ...notification, is_read: true }
              : notification
          );
          return {
            ...prev,
            list: updatedList,
          };
        });

        // 읽지 않은 알림 개수 업데이트
        setUnreadCount((prev) => {
          const newCount = Math.max(0, prev - 1);
          return newCount;
        });
      } catch (error) {
        console.error("알림 읽음 처리 실패:", error);
      }
    },
    [user?.id, setNotificationList, setUnreadCount]
  );

  // 5분마다 알림 목록 갱신
  useEffect(() => {
    if (!user?.id) return;

    // 초기 로드
    fetchNotifications();

    // 5분마다 갱신
    const interval = setInterval(() => {
      fetchNotifications();
    }, 5 * 60 * 1000); // 5분

    return () => clearInterval(interval);
  }, [user?.id, fetchNotifications]);

  return {
    notificationList,
    unreadCount,
    fetchNotifications,
    markAsRead,
  };
};
